import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

export type ReviewCheckId =
    | 'competing-focus'
    | 'phases-not-staged'
    | 'accumulate-only'
    | 'dead-element'
    | 'unnameable-object'
    | 'misaligned-geometry'
    | 'text-hierarchy-mismatch'
    | 'text-split-across-sizes'
    | 'text-clipped'
    | 'content-off-frame'
    | 'content-obscured'
    | 'wrong-copy'
    | 'media-mismatch';

export interface ReviewCheck {
    id: ReviewCheckId;
    /** What the reviewer should look for, phrased as an observation about the frames. */
    fires: string;
    /**
     * Judgeable only on a scene holding an HtmlAsset — a graphic the agent wrote itself — and only
     * across several frames of it. A template scene animates in and settles under a designer's
     * timeline and has no phases to stage, so these checks would report the norm there rather than
     * a defect; and a single captured instant has no motion to read them from either.
     */
    needsGraphicMotion: boolean;
}

export const REVIEW_CHECKS: readonly ReviewCheck[] = [
    {
        id: 'competing-focus',
        fires: 'several elements compete for first read; no single element leads',
        needsGraphicMotion: false,
    },
    {
        id: 'phases-not-staged',
        fires:
            'everything is already present in the first sampled frame and nothing changes for ' +
            'the rest of the scene, or phases land together instead of each taking its own ' +
            'stretch of the timeline',
        needsGraphicMotion: true,
    },
    {
        id: 'accumulate-only',
        fires:
            'the frame ends crowded because it only ever accumulates — across the phases what has ' +
            'made its point stays on screen, at most shrunk or moved aside, instead of leaving',
        needsGraphicMotion: true,
    },
    {
        id: 'dead-element',
        fires:
            'something on screen earns no place — a second idea crowding the first, a label ' +
            'restating the narration, a line saying what the line above it already said, or an ' +
            'element filling a gap',
        needsGraphicMotion: false,
    },
    {
        id: 'unnameable-object',
        fires:
            "a drawn object cannot be named from its shape alone, without reading the scene's " +
            'text or narration for a hint — a shape you can only describe ("a ring with marks in ' +
            'it", "a squiggle in a circle") reads as nothing',
        needsGraphicMotion: false,
    },
    {
        id: 'text-hierarchy-mismatch',
        fires:
            'the scene makes TWO OR MORE points but sets one much bigger than the others. ' +
            'FIRST confirm the size gap is real: compare the LETTER HEIGHT of the lines. Bold ' +
            'weight, a different typeface, a colour change, or a highlight chip behind a line ' +
            'are styling, NOT size — if the letters are about the same height, there is no gap ' +
            'and you must NOT report it, however much one line stands out. Once a real gap is ' +
            'confirmed, transcribe the lines and ask whether the smaller one is genuinely ' +
            'SUBORDINATE to the larger — does it elaborate, qualify, attribute or label it? If ' +
            'it does, that is correct hierarchy and you must NOT report it. If instead they ' +
            'stand as separate points of their own — two claims, two credentials, two parallel ' +
            'clauses, two steps of a process — they are coordinate, the size gap ranks them for ' +
            'no reason, and this fires. A LIST is the clearest case: whenever the scene shows ' +
            'three or more short items of the same kind (qualities, services, steps, features, ' +
            'credentials), they are coordinate by construction and must all be set at ONE size. ' +
            'Count the items and compare their letter heights — if any item is set larger or ' +
            'smaller than its fellows, report it, naming which item is out of step',
        needsGraphicMotion: false,
    },
    {
        id: 'text-split-across-sizes',
        fires:
            'ONE sentence is spread over two text assets of very different size. FIRST confirm ' +
            'the size gap is real by comparing LETTER HEIGHT — a bold face, a script face, a ' +
            'colour change or a highlight chip is styling, not a size gap, and a sentence set ' +
            'in two styles at ONE size must NOT be reported. Then transcribe both lines and ' +
            'read them as a single string: if that string is one grammatical sentence, this ' +
            'fires — the halves of one thought have been assigned different importance. Report ' +
            'it even though the sentence reads correctly in order; the defect is the demotion, ' +
            'not legibility. Two exceptions you must NOT report: (a) the smaller line sits ' +
            'ABOVE the larger one and both are short — a deliberate lead-in into a payoff; ' +
            '(b) the enlarged element is a NUMBER or short statistic and the words around it ' +
            'are its labels at a consistent smaller size — that is the standard way to set a ' +
            'statistic, and the figure is meant to carry the emphasis',
        needsGraphicMotion: false,
    },
    {
        id: 'misaligned-geometry',
        fires:
            'a rule, axis or baseline passes through the shapes it should sit under or beside ' +
            'instead of meeting their edge; a hand, needle, dot or hub sits away from the ' +
            'centre of the ring or dial it belongs to; elements that plainly form a row, column ' +
            'or chart do not share an edge or an even spacing. Name both elements and which way ' +
            'the offset runs ("the orange baseline crosses the bars about a fifth of the way up")',
        needsGraphicMotion: true,
    },
    {
        id: 'text-clipped',
        fires:
            'text overflowing its box, cut off, too small to read, or spilling outside a shape ' +
            'that bounds it (a circle, badge or pill)',
        needsGraphicMotion: false,
    },
    {
        id: 'content-off-frame',
        fires: 'any element cut off by a frame edge — not only text',
        needsGraphicMotion: false,
    },
    {
        id: 'content-obscured',
        fires:
            "an element covers a face, the media's subject, or another element, in a way that reads " +
            'as unintended — copy lying across a bar, ring or shape, or a shape that grows over a ' +
            'caption, counts even when the words stay legible; a chip, card or pill behind the copy, ' +
            'or a ring or rule centred on it, is deliberate and does not',
        needsGraphicMotion: false,
    },
    {
        id: 'wrong-copy',
        fires:
            'copy at rest that does not read as what the narration says: a word with jumbled, ' +
            'repeated or missing letters ("RECOIRECOI" for "records"), two words drawn over each ' +
            'other, or a number with other digits than the narration gives for it, a dropped or ' +
            'extra digit, or the decimal point moved (the narration says "17.3 percent" and the ' +
            'frame settles on "06.2%"). The same words or figure written another way ("4.1 ' +
            'billion" as "4.1B") is not a finding, and nor is copy caught mid-scramble, mid-roll or ' +
            'mid-count while it is still on its way to the words it lands on',
        needsGraphicMotion: false,
    },
    {
        id: 'media-mismatch',
        fires:
            'a photo or video whose subject is not what the scene is about: a different thing from ' +
            'the text or narration; a lightbulb/rocket/gears metaphor standing in for it; or a ' +
            'workplace default — a smiling worker at a desk, a posed team, a handshake — under a ' +
            'line that never put the scene in a workplace. When the line is about an office, a ' +
            'team, software or a business, a workplace shot is the subject, however stock it looks. ' +
            'Only when the line names nothing you could photograph, judge the footage against the ' +
            "video's subject instead: footage that fits the video is not a defect",
        needsGraphicMotion: false,
    },
];

export interface ReviewFinding {
    check: ReviewCheckId;
    scene_id: string;
    detail: string;
    context?: string;
}

export function applicableChecks({
    hasGraphicMotion,
}: {
    /** One flag, not two: a template scene and a single captured instant rule out the same checks. */
    hasGraphicMotion: boolean;
}): readonly ReviewCheck[] {
    return REVIEW_CHECKS.filter((check) => !check.needsGraphicMotion || hasGraphicMotion);
}

/**
 * The reviewer's brief. The closed list is deliberately the only bar: a "don't report it if you'd
 * caveat it" threshold on top of it suppressed real defects and no commentary at all.
 */
export function buildReviewInstruction(
    checks: readonly ReviewCheck[],
    priorFindings: readonly ReviewFinding[] = [],
    videoSubject?: string,
): string {
    const list = checks.map((check) => `- ${check.id}: ${check.fires}`).join('\n');
    // A scene's own line is often too abstract to say what footage belongs on it ("Launching
    // soon."), so relevance is judged against the video as a whole, not the line alone.
    const subject = videoSubject?.trim() ? `The video is about: ${videoSubject.trim()}\n\n` : '';
    // Never the previous reviewer's own sentence, and never that an edit was attempted: given
    // either, the model hands the finding back instead of looking. The check id is the whole hint.
    const priorLines = [
        ...new Set(
            priorFindings.map((finding) => `- [${finding.check}] scene ${finding.scene_id}`),
        ),
    ];
    const prior = priorLines.length
        ? `\n\nAn earlier review of these scenes reported these checks:
${priorLines.join('\n')}

Look at each one against the frames above, and judge it the way you would on a first review:
describe what these frames actually show, then decide. Being named here is not evidence that a
check still fires — report it again only if you can see it in the frames you were given.`
        : '';
    // toolNameReferences.spec.ts forbids a literal tool-name string anywhere under src/agent —
    // the name must come from TOOL_NAMES so a rename propagates.
    return `${subject}Review the frames above against this closed list of defects.

${list}

Two things on screen are intentional. Stock media carries a watermark in these editor renders and
is licensed and unwatermarked at render time. A small logo held in a corner across scenes is the
video's own brand watermark, placed on purpose. Never report either.

Report nothing that is not on the list, however much it stands out. A scene with nothing to say
about it gets an empty list.${prior}

Judge each one on its own terms. Do not weigh what happens next.

Report every finding through the ${TOOL_NAMES.reportFindings} tool. Use the scene_id exactly as it is labelled above,
and make each detail concrete enough to act on without seeing the frames.`;
}

/**
 * Order-independent identity for a finding set, including its free-form `detail` text. Used only
 * to call out an edit that provably changed nothing — the wording must match byte-for-byte for
 * that claim to be honest, since the reviewer is re-describing freshly rendered frames each round
 * and any wording drift means it actually looked again.
 */
export function findingsSignature(findings: readonly ReviewFinding[]): string {
    return findings
        .map((finding) => `${finding.check}|${finding.scene_id}|${finding.detail}`)
        .sort()
        .join('\n');
}

/**
 * Checks whose fix is a real procedure rather than something the reviewer's one-sentence `detail`
 * can carry. The `detail` is free text the vision model writes fresh each round, so without this
 * the agent improvises a remedy from a description of the defect — which is how the contrast doom
 * loop (AP-926) happened. Matches how the widow lint and set_text point at fix-text-wrapping.
 */
const FIX_SKILL_BY_CHECK: Partial<Record<ReviewCheckId, string>> = {
    'text-hierarchy-mismatch': SKILL_NAMES.fixTextHierarchy,
    'text-split-across-sizes': SKILL_NAMES.fixTextHierarchy,
    'text-clipped': SKILL_NAMES.fixTextWrapping,
    'misaligned-geometry': SKILL_NAMES.motionGraphicsScenesBasics,
};

/**
 * Closes every failing gate. Left to itself the agent fixes, waits a turn, re-reviews, waits
 * another, then lints — three model round trips where one would do, since edits in a turn run
 * before the review and lint in that same turn. A skill load is the one thing that must come
 * first: `load_skill` is not deferred, so a fix sent alongside it is written before the
 * procedure comes back. When a finding names a skill, the note says so rather than reading as
 * "skip the skill".
 */
function nextTurnNote(findings: readonly ReviewFinding[]): string {
    const namesSkill = findings.some((finding) => FIX_SKILL_BY_CHECK[finding.check]);
    const timing = namesSkill
        ? 'in your next turn (after loading any skill named above, if you have not already)'
        : 'in your next turn';
    return (
        `Send every fix, the re-review of the scenes you changed, and ${TOOL_NAMES.lintVideo} together ` +
        `${timing}. Edits run before the review and lint, so they judge the fixed scenes.`
    );
}

export function formatReviewGate(
    findings: readonly ReviewFinding[],
    options: { repeated: boolean },
): string {
    if (findings.length === 0) {
        return 'Review found no defects.';
    }
    const lead = options.repeated
        ? `Review found the same ${findings.length} defect(s), unchanged from your previous attempt — that edit did not affect them. Look at the scene and change your approach instead of resending an equivalent fix:`
        : `Review found ${findings.length} defect(s) that must be fixed:`;
    const list = findings
        .map(
            (finding) =>
                `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}${finding.context ?? ''}${FIX_SKILL_BY_CHECK[finding.check] ? ` Load the ${FIX_SKILL_BY_CHECK[finding.check]} skill for the fix procedure.` : ''}`,
        )
        .join('\n');
    return `${lead}\n${list}\n${nextTurnNote(findings)}`;
}
